import { SagaIterator } from 'redux-saga';
import { all, call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import {
  PapiUser,
  PapiDaSharedUserFriends,
  PapiDaSharedUserFriendsforletter,
  PapiRequestDaSharedUserFriends,
  PapiRequestDaSharedUserFriendsforletter,
} from '@wix/da-papi-types';

// TODO: remove once BE task is done
type PapiDaSharedUserSupporters = PapiDaSharedUserFriends & {
  supporters: PapiUser[];
};

import { requestPuppy } from '@wix/da-http-client';
import {
  blockUser,
  blockUserAndRedirect,
  blockUserFail,
  blockUserSuccess,
  fetchMoreFriends,
  fetchMoreSupporters,
  initializeFriendList,
  searchFriendsWithOptions,
  unblockUser,
  unblockUserFail,
  unblockUserSuccess,
} from './actionCreators';
import ErrorIcon from '@wix/da-ds/pkg/Icons/24x24/Error';
import { addNotification } from '../../Notifications/redux/actionCreators';
import { NotificationType } from '../../Notifications';
import { selectors as streamSelectors, withOffset } from '../../Stream';
import { normalize } from 'normalizr';
import { userListSchema } from '../normalizr/schemas/user';
import { friendsStreamId, supportersStreamId } from './constants';
import { FetchType } from '../../Stream/base/types';

const logger = ddt.createLogger('usersaga');

export function* doBlockUser(userIdOrUuid, chatroomId?) {
  return yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/user/block',
      data: {
        userid: userIdOrUuid,
        chatroom_id: chatroomId,
      },
    },
    () => ({ success: true })
  );
}

function* blockUserSaga(action: ReturnType<typeof blockUser>) {
  if (!action.payload) {
    logger.error('Tried to block invalid user', action);
    return;
  }
  const { user } = action.payload;
  const result = yield call(doBlockUser, user.userId);

  if (result && result.success) {
    yield put(blockUserSuccess(user.userId));
    if (action.payload.dispatchAction) {
      yield put(action.payload.dispatchAction);
    }
  } else {
    yield put(blockUserFail(user, result));
  }
}

function* blockUserAndRedirectSaga(action: ReturnType<typeof blockUserAndRedirect>) {
  if (!action.payload) {
    logger.error('Tried to block invalid userId', action);
    return;
  }
  const { user, redirectLocation } = action.payload;

  const result = yield call(doBlockUser, user.userId);

  if (result && result.success) {
    yield put(blockUserSuccess(user.userId));
    window.location.href = redirectLocation;
  } else {
    yield put(blockUserFail(user, result));
  }
}

function* unblockUserSaga(action: ReturnType<typeof unblockUser>) {
  if (!action.payload) {
    logger.error('Tried to unblock invalid userId', action);
    return;
  }
  const userId = action.payload;

  const result = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/user/unblock',
      data: {
        userid: userId,
      },
    },
    () => ({ success: true })
  );

  if (result && result.success) {
    yield put(unblockUserSuccess(userId));
  } else {
    yield put(unblockUserFail(userId, result));
  }
}

function* handleBlockUserSuccess() {
  yield put(
    addNotification(NotificationType.Generic, {
      messageLocaleKey: 'common.notification.blockUser.success',
    })
  );
}

function* handleBlockUserFail(action: ReturnType<typeof blockUserFail>) {
  const { user, result } = action.payload;

  // common.notification.blockUser.fail.default etc
  const key = result?.errorDetails?.reason?.toLowerCase() ?? 'default';

  yield put(
    addNotification(NotificationType.Generic, {
      messageLocaleKey: `common.notification.blockUser.fail.${key}`,
      messageLocaleOptions: { username: user.username },
      isError: true,
      cardProps: {
        iconComponent: ErrorIcon,
        multiLine: true,
        limitTextWidth: false,
        duration: 5000,
      },
    })
  );
}

function* handleSearchFriends({
  payload: {
    query,
    callback,
    isChatContext = false,
    includeNonFriendExactMatch,
    userType,
    excludeBanned = false,
  },
}: ReturnType<typeof searchFriendsWithOptions>) {
  // debounce for fast typing
  yield delay(300);
  query = query.trim().toLowerCase();
  if (query.length < 3) {
    callback([]);
    return;
  }

  const params: PapiRequestDaSharedUserFriendsforletter = {
    search: includeNonFriendExactMatch ? query : undefined,
    letter: query[0],
    page: 0,
    is_chat: isChatContext,
    user_type: userType,
  };

  const result: PapiDaSharedUserFriendsforletter = yield call(
    requestPuppy,
    {
      method: 'get',
      url: `/user/friendsforletter`,
      params,
    },
    () => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line global-require
        const { users } = require('@wix/da-sample-data');
        return { users };
      }
    }
  );
  if (!result || !result.users || result.users.length === 0) {
    callback([]);
  }
  let found = (result.users || [])
    // include only those usernames that actually start with query
    .filter(u => u.username.toLowerCase().startsWith(query))
    // unique
    .filter((u, idx, self) => self.findIndex(u2 => u.userId === u2.userId) === idx);

  if (excludeBanned) {
    found = found.filter(u => !u.isBanned);
  }

  callback(found);
}

function* handleFetchMoreFriends({ payload: { streamId } }: ReturnType<typeof fetchMoreFriends>) {
  return yield call(deviantsStreamFetchMore, streamId);
}
function* handleFetchMoreSupporters({
  payload: { streamId },
}: ReturnType<typeof fetchMoreSupporters>) {
  return yield call(deviantsStreamFetchMore, streamId);
}

function* deviantsStreamFetchMore(streamId: string) {
  const { offset, limit } = yield select(state => ({
    offset: withOffset.selectors.getNextOffset(state, streamId),
    limit: streamSelectors.getItemsPerFetch(state, streamId),
  }));

  const params: PapiRequestDaSharedUserFriends = {
    user_type: 'deviant',
    offset,
    limit,
  };

  let response: PapiDaSharedUserFriends | PapiDaSharedUserSupporters | undefined = undefined;
  let results: PapiUser[] = [];

  if (streamId === friendsStreamId) {
    const friendsResults: PapiDaSharedUserFriends = yield call(
      requestPuppy,
      {
        method: 'get',
        url: '/user/friends',
        params,
      },
      () => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line global-require
          const { users } = require('@wix/da-sample-data');
          return { results: users, hasMore: false };
        }
      }
    );
    response = friendsResults;
    results = response.results || [];
  }

  if (streamId === supportersStreamId) {
    const supportersResults: PapiDaSharedUserSupporters = yield call(
      requestPuppy,
      {
        method: 'get',
        url: '/user/supporters',
        params,
      },
      () => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line global-require
          const { users } = require('@wix/da-sample-data');
          return { results: users, hasMore: false };
        }
      }
    );
    response = supportersResults;
    results = supportersResults.supporters || [];
  }

  if (!response) {
    yield put(
      withOffset.actions.fetchFailure({
        streamId,
        fetchType: FetchType.Next,
        errorMsg: 'fetch error',
      })
    );
    return;
  }

  const { entities, result: items } = normalize(results, userListSchema);

  yield put(
    withOffset.actions.fetchSuccess({
      streamId,
      items,
      entities,
      hasMore: response?.hasMore || false,
    })
  );
}

function* handleDeviantsStreamInit({ payload: { streamId } }) {
  const isAlreadyInit = yield select(streamSelectors.isInitialized, streamId);
  if (isAlreadyInit) {
    return;
  }

  if (streamId === friendsStreamId) {
    yield put(
      withOffset.actions.initialize({
        streamId,
        itemsPerFetch: 50,
        hasMore: true,
        fetchNextCallback: fetchMoreFriends,
      })
    );
  }

  if (streamId === supportersStreamId) {
    yield put(
      withOffset.actions.initialize({
        streamId,
        itemsPerFetch: 50,
        hasMore: true,
        fetchNextCallback: fetchMoreSupporters,
      })
    );
  }
}

export default function* saga(): SagaIterator {
  yield all([
    takeLatest(blockUser, blockUserSaga),
    takeLatest(unblockUser, unblockUserSaga),
    takeLatest(blockUserAndRedirect, blockUserAndRedirectSaga),
    takeLatest(blockUserSuccess, handleBlockUserSuccess),
    takeLatest(blockUserFail, handleBlockUserFail),
    takeLatest(searchFriendsWithOptions, handleSearchFriends),
    takeEvery(fetchMoreFriends, handleFetchMoreFriends),
    takeEvery(fetchMoreSupporters, handleFetchMoreSupporters),
    takeEvery(initializeFriendList, handleDeviantsStreamInit),
  ]);
}
